import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Check, ChevronRight, Plus, Star, Users, Tag, Store, Package, IndianRupee, MessageSquare, X, Navigation, Settings, Smartphone, Heart, QrCode } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { NewArrivals } from '../components/smartqr/NewArrivals';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { DealCard } from '../components/common/DealCard';
import { QRCodeModal } from '../components/modals/QRCodeModal';
import { cities, getAreasForCity, offerTypes, UPI_ID, PAYEE_NAME, WHATSAPP_NUMBER, FEATURED_AMOUNT } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const HomePage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedCity, setSelectedCity] = useState(''); // Default to empty - show all
  const [selectedArea, setSelectedArea] = useState('');
  const [locationStatus, setLocationStatus] = useState('idle');
  const [maxDistance, setMaxDistance] = useState(10);
  const [showFeaturedQR, setShowFeaturedQR] = useState(false);
  const [showSupportQR, setShowSupportQR] = useState(false);
  const [featuredPaymentInitiated, setFeaturedPaymentInitiated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleDeals, setVisibleDeals] = useState(25);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationErrorType, setLocationErrorType] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setSelectedArea('');
    fetchDeals(null, null, null, null, city);
  };

  const handleFeaturedPayment = () => {
    const upiLink = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(PAYEE_NAME)}&am=${FEATURED_AMOUNT}&cu=INR&tn=${encodeURIComponent('Featured Listing - JustOffs')}`;
    if (isMobile) {
      toast.info('Redirecting to UPI app...');
      window.location.href = upiLink;
      setTimeout(() => { setFeaturedPaymentInitiated(true); toast.success('Complete payment in your UPI app'); }, 1500);
    } else {
      setShowFeaturedQR(true);
    }
  };

  const handleWhatsAppConfirm = () => {
    const message = encodeURIComponent(`Hi! I have paid ₹${FEATURED_AMOUNT} for JustOffs Featured Listing. Please confirm and activate my featured listing.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const budgetFilters = [
    { label: 'Under ₹100', value: 100 },
    { label: 'Under ₹200', value: 200 },
    { label: 'Under ₹300', value: 300 }
  ];

  const distanceOptions = [
    { label: 'Within 1 km', value: 1 },
    { label: 'Within 3 km', value: 3 },
    { label: 'Within 5 km', value: 5 },
    { label: 'Within 10 km', value: 10 }
  ];

  useEffect(() => { fetchDeals(); }, []); // Fetch all deals on load

  const fetchDeals = async (lat, lng, area, distance = null, city = null, retryCount = 0) => {
    setLoading(true);
    try {
      let url = `${API}/api/deals?limit=25&`;
      // Only add city filter if city is selected
      if (city) url += `city=${encodeURIComponent(city)}&`;
      if (lat && lng && distance) url += `lat=${lat}&lng=${lng}&max_distance=${distance}&`;
      else if (lat && lng) url += `lat=${lat}&lng=${lng}&`;
      if (area) url += `area=${encodeURIComponent(area)}&`;
      url += `_t=${Date.now()}`;
      console.log('Fetching deals from:', url);
      const res = await axios.get(url, { timeout: 10000 });
      // Handle both old array format and new paginated format
      const dealsData = res.data.deals || res.data;
      console.log('Deals loaded:', dealsData.length);
      setDeals(dealsData);
    } catch (e) {
      console.error('Failed to fetch deals:', e);
      if (retryCount < 3) {
        setTimeout(() => fetchDeals(lat, lng, area, distance, city, retryCount + 1), 1000);
        return;
      }
      toast.error('Failed to load deals. Please refresh the page.');
    }
    setLoading(false);
  };

  const requestLocation = () => {
    setLocationStatus('requesting');
    
    // Check if geolocation is available
    if (!navigator.geolocation) {
      setLocationStatus('unsupported');
      setLocationErrorType('unsupported');
      setShowLocationModal(true);
      return;
    }
    
    // Request location with options optimized for mobile
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationStatus('granted');
        fetchDeals(pos.coords.latitude, pos.coords.longitude, null, maxDistance, selectedCity);
        toast.success(`Found deals within ${maxDistance}km of you!`);
      },
      (error) => {
        console.error('Location error:', error.code, error.message);
        setLocationStatus('denied');
        
        // Show modal with specific error type
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationErrorType('denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationErrorType('unavailable');
            break;
          case error.TIMEOUT:
            setLocationErrorType('timeout');
            break;
          default:
            setLocationErrorType('unknown');
        }
        setShowLocationModal(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  const handleDistanceChange = (distance) => {
    setMaxDistance(distance);
    if (userLocation) {
      fetchDeals(userLocation.lat, userLocation.lng, null, distance, selectedCity);
      toast.success(`Showing deals within ${distance}km`);
    }
  };

  const handleAreaSelect = (areaName) => { 
    setSelectedArea(areaName);
    if (areaName) {
      fetchDeals(null, null, areaName, null, selectedCity);
    } else {
      // When "All Areas" is selected, fetch deals for the city (or all if no city)
      fetchDeals(null, null, null, null, selectedCity || null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <SEO title="JustOffs - India's First Free Classified Platform for Deals & Offers" description="Discover affordable food, fashion, and local deals near you in Pune. Save money and support local businesses with JustOffs." />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 px-4 py-2 rounded-full mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
            </span>
            <span className="text-orange-300 text-xs sm:text-sm font-medium tracking-wide">
              India's First Free Classified Platform for Local Deals
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 text-white leading-tight">Find the Best Local Deals <br className="hidden sm:block" /><span className="text-white/90">Near You</span></h1>
          <p className="text-slate-300 text-sm sm:text-base mb-5 max-w-xl mx-auto">Affordable food, fashion & local services from trusted businesses near you.</p>
          
          <div className="flex flex-row gap-2 sm:gap-3 justify-center items-center max-w-md mx-auto">
            <button onClick={requestLocation} disabled={locationStatus === 'requesting'}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-6 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition disabled:opacity-70" data-testid="use-location-btn">
              <MapPin className="w-4 h-4" /> 
              {locationStatus === 'requesting' ? 'Finding...' : 'Near Me'}
            </button>
            <span className="text-slate-500 text-xs">or</span>
            <select value={selectedCity} onChange={(e) => handleCityChange(e.target.value)}
              className="flex-1 bg-white text-slate-700 px-4 py-3 rounded-lg font-medium text-sm appearance-none cursor-pointer" data-testid="city-select">
              <option value="">Select City</option>
              {cities.filter(city => city !== 'Other').map(city => (<option key={city} value={city}>{city}</option>))}
            </select>
          </div>
          
          {/* Location status hints */}
          {locationStatus === 'idle' && (
            <p className="text-slate-400 text-xs mt-3 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> Tap "Near Me" to discover deals around you
            </p>
          )}

          {locationStatus === 'granted' && (
            <p className="text-green-400 text-sm flex items-center justify-center gap-2 mt-4"><Check className="w-4 h-4" /> Location detected - showing nearby deals</p>
          )}
        </div>
      </section>

      {/* Smart Business QR CTA (homepage only) */}
      <section className="bg-slate-800 py-3" data-testid="smart-qr-cta-strip">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white text-sm text-center sm:text-left"><span className="font-bold">Shop owner?</span> <span className="text-slate-300">Get a free business page + permanent QR in 2 minutes.</span></p>
          <Link to="/smart-business-qr" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition whitespace-nowrap w-full sm:w-auto justify-center" data-testid="home-get-smart-qr-btn">
            <QrCode className="w-4 h-4" /> GET SMART QR
          </Link>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-white py-6 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-center gap-6 sm:gap-12 md:gap-16">
            <div className="text-center"><div className="flex items-center justify-center gap-1.5 text-slate-800 mb-1"><Store className="w-4 h-4 text-slate-400" /><span className="text-xl font-bold">500+</span></div><p className="text-xs text-slate-500">Shops</p></div>
            <div className="text-center"><div className="flex items-center justify-center gap-1.5 text-slate-800 mb-1"><Tag className="w-4 h-4 text-slate-400" /><span className="text-xl font-bold">2000+</span></div><p className="text-xs text-slate-500">Offers</p></div>
            <div className="text-center"><div className="flex items-center justify-center gap-1.5 text-slate-800 mb-1"><Users className="w-4 h-4 text-slate-400" /><span className="text-xl font-bold">10K+</span></div><p className="text-xs text-slate-500">Users</p></div>
            <div className="text-center"><div className="flex items-center justify-center gap-1.5 text-slate-800 mb-1"><Check className="w-4 h-4 text-green-500" /><span className="text-xl font-bold">Free</span></div><p className="text-xs text-slate-500">Always</p></div>
          </div>
        </div>
      </section>

      {/* New Arrivals - Smart Business QR shops */}
      <NewArrivals city={selectedCity} />

      {/* Deals */}
      <section className="py-8 md:py-10 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header Row - Mobile Optimized */}
          <div className="flex flex-col gap-4 mb-6">
            {/* Title and View All */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-bold text-slate-800">Latest Deals</h2>
              <Link to="/deals" className="bg-orange-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-1 text-sm hover:bg-orange-600 transition" data-testid="view-all-link">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Filter Row - Horizontal Scroll on Mobile */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
              {userLocation && (
                <select value={maxDistance} onChange={(e) => handleDistanceChange(Number(e.target.value))} 
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600 cursor-pointer hover:border-orange-400 flex-shrink-0" data-testid="distance-select">
                  {distanceOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                </select>
              )}
              {selectedCity && (
                <select value={selectedArea} onChange={(e) => handleAreaSelect(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600 cursor-pointer hover:border-orange-400 flex-shrink-0" data-testid="area-select">
                  <option value="">All Areas</option>
                  {getAreasForCity(selectedCity).map(area => (<option key={area.id} value={area.name}>{area.name}</option>))}
                </select>
              )}
              <select onChange={(e) => e.target.value && navigate(`/deals?offer_type=${encodeURIComponent(e.target.value)}`)} 
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600 cursor-pointer hover:border-orange-400 flex-shrink-0 min-w-[140px]" data-testid="offer-type-select">
                <option value="">Refine by Type</option>
                {offerTypes.map(type => (<option key={type.id} value={type.id}>{type.emoji} {type.name}</option>))}
              </select>
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100 animate-pulse">
                  <div className="h-40 bg-slate-200"></div>
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-full"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : deals.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="deals-grid">
                {deals.slice(0, visibleDeals).map(deal => (<DealCard key={deal.id} deal={deal} />))}
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                {visibleDeals < deals.length && (
                  <button onClick={() => setVisibleDeals(prev => prev + 25)} className="w-full sm:w-auto bg-white border-2 border-slate-200 text-slate-700 px-6 py-2.5 rounded-xl font-semibold hover:border-orange-400 hover:text-orange-600 transition" data-testid="load-more-btn">
                    Load More Deals
                  </button>
                )}
                <Link to="/deals" className="w-full sm:w-auto text-center bg-orange-500 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-orange-600 transition flex items-center justify-center gap-2" data-testid="view-all-btn">
                  View All Deals <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No deals nearby</h3>
              <p className="text-slate-500 text-sm mb-4">{userLocation ? `No deals found within ${maxDistance}km. Try increasing the distance.` : 'Select an area or use your location to find deals.'}</p>
              <Link to="/post-ad" className="inline-flex items-center gap-2 bg-orange-500 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-orange-600 transition">
                <Plus className="w-4 h-4" /> Post Free Ad
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Budget Filters */}
      <section className="py-10 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-800 mb-5 text-center">Shop by Budget</h2>
          <div className="flex justify-center gap-3 flex-wrap">
            {budgetFilters.map(budget => (
              <Link key={budget.value} to={`/deals?max_price=${budget.value}`}
                className="bg-orange-500 text-white px-6 py-3 rounded-lg text-center hover:bg-orange-600 transition" data-testid={`budget-${budget.value}`}>
                <span className="font-semibold">{budget.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Get Featured Section */}
      <section className="py-10 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl shadow-sm p-6 border border-orange-200">
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Star className="w-7 h-7 text-white fill-white" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-lg font-bold text-slate-800 mb-1">Get Featured on JustOffs</h2>
                <p className="text-slate-600 text-sm">Promote your business for better visibility</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-orange-500">₹499</span>
                <span className="bg-orange-100 text-orange-600 text-xs px-3 py-1 rounded-full font-medium">Valid for 1 month</span>
              </div>
              <p className="text-sm text-slate-600">Your listing will appear at the top with a "Featured" badge</p>
            </div>
            <button onClick={handleFeaturedPayment} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition flex items-center justify-center gap-2 shadow-md" data-testid="get-featured-btn">
              <IndianRupee className="w-5 h-5" /> Get Featured Now
            </button>
            {featuredPaymentInitiated && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-700 text-sm mb-3">Complete payment in your UPI app</p>
                <button onClick={handleWhatsAppConfirm} className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-green-600 transition flex items-center justify-center gap-2 mx-auto" data-testid="whatsapp-confirm-btn">
                  <MessageSquare className="w-4 h-4" /> Confirm on WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* QR Code Modal for Featured */}
      <QRCodeModal 
        show={showFeaturedQR}
        onClose={() => setShowFeaturedQR(false)}
        amount={FEATURED_AMOUNT}
        title="Get Featured"
        onPaymentComplete={() => { setShowFeaturedQR(false); setFeaturedPaymentInitiated(true); }}
        onWhatsAppConfirm={handleWhatsAppConfirm}
      />

      {/* Support Us Section */}
      <section className="py-8 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl shadow-sm p-6 border border-pink-200">
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-white fill-white" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-lg font-bold text-slate-800 mb-1">Support JustOffs</h2>
                <p className="text-slate-600 text-sm">Help us keep this platform free for everyone</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4 text-center sm:text-left">
              JustOffs is 100% free for shopkeepers and buyers. Your small contribution helps us maintain servers, add new features, and expand to more cities.
            </p>
            <button 
              onClick={() => setShowSupportQR(true)} 
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-3 rounded-xl font-semibold hover:from-pink-600 hover:to-rose-600 transition flex items-center justify-center gap-2 shadow-md" 
              data-testid="support-us-btn"
            >
              <Heart className="w-5 h-5" /> Support Us
            </button>
          </div>
        </div>
      </section>

      {/* QR Code Modal for Support Us */}
      <QRCodeModal 
        show={showSupportQR}
        onClose={() => setShowSupportQR(false)}
        amount={99}
        title="Support JustOffs"
        onPaymentComplete={() => { setShowSupportQR(false); toast.success('Thank you for your support! 🙏'); }}
        onWhatsAppConfirm={() => {
          const message = encodeURIComponent(`Hi! I have supported JustOffs. Thank you for building this platform!`);
          window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
        }}
      />

      {/* Location Permission Modal - Compact & Clean */}
      {showLocationModal && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 animate-[fadeIn_0.2s_ease-out]" 
          onClick={() => setShowLocationModal(false)}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xs overflow-hidden shadow-xl animate-[slideUp_0.3s_cubic-bezier(0.16,1,0.3,1)] sm:m-4" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag indicator for mobile */}
            <div className="flex justify-center pt-2 sm:hidden">
              <div className="w-8 h-1 bg-slate-200 rounded-full"></div>
            </div>
            
            {/* Content */}
            <div className="p-5 text-center">
              {/* Icon */}
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              
              <h3 className="text-lg font-bold text-slate-800 mb-1">Enable Location</h3>
              
              {locationErrorType === 'denied' && (
                <p className="text-slate-500 text-sm mb-4">
                  Allow location access to find deals near you
                </p>
              )}
              
              {locationErrorType === 'unavailable' && (
                <p className="text-slate-500 text-sm mb-4">
                  Please turn on GPS in your device settings
                </p>
              )}
              
              {locationErrorType === 'timeout' && (
                <p className="text-slate-500 text-sm mb-4">
                  Request timed out. Try again in an open area
                </p>
              )}
              
              {(locationErrorType === 'unsupported' || locationErrorType === 'unknown') && (
                <p className="text-slate-500 text-sm mb-4">
                  Location unavailable. Please select a city
                </p>
              )}
              
              {/* Buttons */}
              <div className="space-y-2">
                <button 
                  onClick={() => { setShowLocationModal(false); setTimeout(() => requestLocation(), 200); }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                >
                  <MapPin className="w-4 h-4" /> Try Again
                </button>
                <button 
                  onClick={() => setShowLocationModal(false)}
                  className="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 py-3 rounded-xl font-medium transition-all active:scale-[0.98]"
                >
                  Select City
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};
